'use client';

import { useEffect, useState } from 'react';
import TaskCard from './TaskCard';

interface Board {
  id: number;
  name: string;
  kanban_column_type: number | null;
  project: number;
}

interface KanbanColumnProps {
  title: string;
  color: string;
  tasks: { id?: number; title: string; taskId: string; assignedTo?: { full_name?: string; email?: string } | null; status?: number; type?: number; estimatedHours?: number | null; childCount?: number; labels?: Array<{ id: number; name: string; color: number; color_display: string }>; release?: { id: number; name: string } | null }[];
  kanbanColumn: number; // 0 = Ideas, 1 = Feature Backlog, 2 = Bugs, 3 = Finalization (project view) OR 0 = Not Started, 1 = In Progress, 2 = Testing, 3 = Done (kanban board)
  board?: Board | null;
  isEditable?: boolean;
  isSprintView?: boolean;
  onTaskClick?: (taskId: number) => void;
  onTaskCreate?: (title: string, kanbanColumn: number) => void;
  onBoardNameUpdate?: (boardId: number, newName: string) => void;
  onTaskDragStart?: (e: React.DragEvent, taskId: number) => void;
  onTaskDrop?: (e: React.DragEvent, taskId: number, newStatus: number) => void;
  onTaskDropToColumn?: (e: React.DragEvent, taskId: number, newKanbanColumn: number) => void;
  onTaskReorder?: (draggedTaskId: number, targetTaskId: number, status: number) => void;
  onRemoveFromSprint?: (taskId: number) => void;
  onBoardDelete?: (boardId: number) => void;
  onBoardMove?: (boardId: number, direction: -1 | 1) => void;
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
}

export default function KanbanColumn({ 
  title, 
  color, 
  tasks, 
  kanbanColumn, 
  board,
  isEditable = false,
  isSprintView = false,
  onTaskClick, 
  onTaskCreate,
  onBoardNameUpdate,
  onTaskDragStart,
  onTaskDrop,
  onTaskDropToColumn,
  onTaskReorder,
  onRemoveFromSprint,
  onBoardDelete,
  onBoardMove,
  canMoveLeft = false,
  canMoveRight = false
}: KanbanColumnProps) {
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);

  // Update editedTitle when title prop changes
  useEffect(() => {
    if (!isEditingTitle) {
      setEditedTitle(title);
    }
  }, [title, isEditingTitle]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newTaskTitle.trim()) {
      if (onTaskCreate) {
        onTaskCreate(newTaskTitle.trim(), kanbanColumn);
      }
      setNewTaskTitle('');
      setIsInputFocused(false);
    } else if (e.key === 'Escape') {
      setNewTaskTitle('');
      setIsInputFocused(false);
    }
  };

  const handleTitleClick = () => {
    if (isEditable && board) {
      setIsEditingTitle(true);
      setEditedTitle(title);
    }
  };

  const handleTitleBlur = () => {
    if (isEditingTitle && board && onBoardNameUpdate) {
      const trimmedTitle = editedTitle.trim();
      if (trimmedTitle && trimmedTitle !== title) {
        onBoardNameUpdate(board.id, trimmedTitle);
      } else {
        setEditedTitle(title);
      }
      setIsEditingTitle(false);
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setEditedTitle(title);
      setIsEditingTitle(false);
      e.currentTarget.blur();
    }
  };


  // For kanban board (sprint view), render without outer container (it's inside a table cell)
  if (isSprintView) {
    const handleDragOver = (e: React.DragEvent) => {
      // Always allow drag over - this enables dropping anywhere in the column
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      const draggedTaskId = e.dataTransfer.getData('taskId');
      if (!draggedTaskId) {
        return;
      }

      // Check if we dropped on a TaskCard
      const target = e.target as HTMLElement;
      const taskCard = target.closest('[data-task-id]');
      
      if (taskCard) {
        const targetTaskId = parseInt(taskCard.getAttribute('data-task-id') || '0');
        if (targetTaskId && targetTaskId !== parseInt(draggedTaskId)) {
          // Check if the dragged task belongs to this column
          const isInThisColumn = tasks.some(t => t.id === parseInt(draggedTaskId));

          if (isInThisColumn && onTaskReorder) {
            // Same-column drop: reorder within column
            onTaskReorder(parseInt(draggedTaskId), targetTaskId, kanbanColumn);
            return;
          }

          // Cross-column drop: change status
          if (onTaskDrop) {
            onTaskDrop(e, parseInt(draggedTaskId), kanbanColumn);
            return;
          }
        }
      }
      
      // Dropped on empty space - change status/column
      if (onTaskDrop) {
        onTaskDrop(e, parseInt(draggedTaskId), kanbanColumn);
      }
    };

    return (
      <div
        style={{
          minHeight: '200px',
          width: '100%',
          height: '100%',
          position: 'relative',
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {tasks.length > 0 ? (
          tasks.map((task, index) => (
            <TaskCard 
              key={task.id || index} 
              title={task.title} 
              taskId={task.taskId}
              taskIdNum={task.id}
              assignedTo={task.assignedTo}
              isSprintView={isSprintView}
              draggable={isSprintView}
              onDragStart={onTaskDragStart ? (e, taskId) => {
                if (typeof taskId === 'number') {
                  onTaskDragStart(e, taskId);
                }
              } : undefined}
              status={task.status}
              type={task.type}
              estimatedHours={task.estimatedHours}
              childCount={task.childCount}
              labels={task.labels}
              release={task.release}
              onRemoveFromSprint={onRemoveFromSprint}
              onClick={() => {
                if (task.id && onTaskClick) {
                  onTaskClick(task.id);
                }
              }}
            />
          ))
        ) : (
          <div className="text-gray-500 text-xs text-center py-4">No tasks</div>
        )}
      </div>
    );
  }

  // Project view: Keep original layout
  return (
    <div
      className="w-64 flex-shrink-0 bg-transparent flex flex-col"
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        const draggedTaskId = e.dataTransfer.getData('taskId');
        if (!draggedTaskId) return;

        // Check if we dropped onto an existing TaskCard
        const target = e.target as HTMLElement;
        const taskCard = target.closest('[data-task-id]');

        if (taskCard) {
          const targetTaskId = parseInt(taskCard.getAttribute('data-task-id') || '0');
          if (targetTaskId && targetTaskId !== parseInt(draggedTaskId)) {
            // If the dragged task is already in this column, reorder within the column
            const isInThisColumn = tasks.some(t => t.id === parseInt(draggedTaskId));
            if (isInThisColumn && onTaskReorder) {
              onTaskReorder(parseInt(draggedTaskId), targetTaskId, kanbanColumn);
              return;
            }
          }
        }

        // Otherwise (empty space or different column) move the task to this column
        if (onTaskDropToColumn) {
          onTaskDropToColumn(e, parseInt(draggedTaskId), kanbanColumn);
        }
      }}
    >
      <div className={`bg-[#0f1429] border border-[#1a1f3a] rounded-lg px-3 py-2 mb-4 flex items-center justify-between hover:border-[#2d1b3d] transition-colors`}>
        {isEditingTitle && isEditable && board ? (
          <input
            type="text"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            className="bg-transparent text-white font-semibold text-sm uppercase tracking-wider flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
            autoFocus
          />
        ) : (
          <h3 
            className="text-white font-semibold text-sm uppercase tracking-wider flex-1 text-center cursor-text hover:text-white transition-colors"
            onClick={handleTitleClick}
            title={isEditable && board ? 'Click to edit board name' : undefined}
          >
            {title}
          </h3>
        )}
        {isEditable && board && (onBoardMove || onBoardDelete) ? (
          <div className="flex items-center gap-0.5">
            {onBoardMove && (
              <>
                <button
                  onClick={() => onBoardMove(board.id, -1)}
                  disabled={!canMoveLeft}
                  title="Move board left"
                  className="text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors px-1"
                >
                  ‹
                </button>
                <button
                  onClick={() => onBoardMove(board.id, 1)}
                  disabled={!canMoveRight}
                  title="Move board right"
                  className="text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors px-1"
                >
                  ›
                </button>
              </>
            )}
            {onBoardDelete && (
              <button
                onClick={() => onBoardDelete(board.id)}
                title="Delete board"
                className="text-gray-400 hover:text-red-400 transition-colors px-1"
              >
                ×
              </button>
            )}
          </div>
        ) : (
          <button className="text-gray-400 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Quick Add Input */}
      <div className="mb-4">
        <input
          type="text"
          value={newTaskTitle}
          onChange={(e) => setNewTaskTitle(e.target.value)}
          onKeyDown={handleKeyPress}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => {
            // Delay blur to allow Enter key to fire
            setTimeout(() => setIsInputFocused(false), 200);
          }}
          placeholder={isInputFocused ? "Enter task title and press Enter" : "Add a task..."}
          className="w-full bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 text-sm transition-colors"
        />
      </div>

      <div className="pr-2">
        {tasks.length > 0 ? (
          tasks.map((task, index) => (
            <TaskCard 
              key={task.id || index} 
              title={task.title} 
              taskId={task.taskId}
              taskIdNum={task.id}
              assignedTo={task.assignedTo}
              isSprintView={isSprintView}
              draggable={true}
              onDragStart={onTaskDragStart ? (e, taskId) => {
                if (typeof taskId === 'number') {
                  onTaskDragStart(e, taskId);
                }
              } : undefined}
              status={task.status}
              type={task.type}
              estimatedHours={task.estimatedHours}
              childCount={task.childCount}
              labels={task.labels}
              release={task.release}
              onClick={() => {
                if (task.id && onTaskClick) {
                  onTaskClick(task.id);
                }
              }}
            />
          ))
        ) : (
          <div className="text-gray-500 text-xs text-center py-4">No tasks</div>
        )}
      </div>
    </div>
  );
}

